/* eslint-disable react/prop-types */

import React from 'react';
import { Form, Col } from 'react-bootstrap';
import Select from 'react-select';
import { useFormContext, Controller } from 'react-hook-form';
import { FormLabel } from './FormLabel';
import ErrorFeedback from './ErrorFeedback';

/**
 * TextAndUnitField - Combines a text input and a dropdown (react-select) in a single Form.Group
 * Always uses 'quantity' and 'unit' as fixed keys
 * @param {object} props - expects metadata, namePrefix, errors, isDisabled
 */
const TextAndUnitField = ({ metadata, namePrefix, errors, isDisabled }) => {
  const { control, setValue, getValues } = useFormContext();
  const [isInitialized, setIsInitialized] = React.useState(false);

  const defaultObj = metadata?.defaultValue || {};

  // Fixed field names - always use 'quantity' and 'unit'
  const quantityFieldName = `${namePrefix}.quantity`;
  const unitFieldName = `${namePrefix}.unit`;

  const options = Array.isArray(metadata.options)
    ? metadata.options.flatMap(opt => (opt.options ? opt.options : [opt]))
    : [];

  // Only set default values once on initial mount
  React.useEffect(() => {
    if (!isInitialized) {
      const currentValues = getValues();
      const currentQuantity = currentValues[namePrefix]?.quantity;
      const currentUnit = currentValues[namePrefix]?.unit;

      // Only set defaults if fields are empty/undefined
      if (
        defaultObj.quantity !== undefined &&
        (currentQuantity === undefined || currentQuantity === '')
      ) {
        setValue(quantityFieldName, defaultObj.quantity, {
          shouldValidate: false,
          shouldDirty: false
        });
      }

      if (
        defaultObj.unit !== undefined &&
        (currentUnit === undefined || currentUnit === '')
      ) {
        setValue(unitFieldName, defaultObj.unit, {
          shouldValidate: false,
          shouldDirty: false
        });
      }

      setIsInitialized(true);
    }
  }, [
    isInitialized,
    defaultObj,
    setValue,
    quantityFieldName,
    unitFieldName,
    namePrefix,
    getValues
  ]);

  return (
    <Col>
      <Form.Group className="mb-3">
        <FormLabel
          label={metadata.label}
          required={metadata?.validation?.required}
        />
        <div className="d-flex gap-2">
          <div style={{ flex: '2' }}>
            <Controller
              name={quantityFieldName}
              control={control}
              defaultValue={defaultObj.quantity || ''}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="number"
                  placeholder={metadata.placeholder}
                  onWheel={e => e.target.blur()} // Prevents scroll on number input
                  isInvalid={
                    !!(
                      errors?.[namePrefix]?.quantity ||
                      errors?.[namePrefix]?.unit
                    )
                  }
                  disabled={isDisabled}
                  style={{
                    height: '38px'
                  }}
                />
              )}
            />
          </div>
          <div style={{ flex: '1', minWidth: '120px' }}>
            <Controller
              name={unitFieldName}
              control={control}
              defaultValue={defaultObj.unit || ''}
              render={({ field }) => (
                <Select
                  {...field}
                  options={options}
                  value={options.find(opt => opt.value === field.value) || null}
                  onChange={selected => {
                    field.onChange(selected ? selected.value : '');
                  }}
                  isDisabled={isDisabled}
                  placeholder="Select unit"
                  classNamePrefix="react-select"
                  isClearable={false}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: '38px',
                      height: '38px',
                      borderColor: state.isFocused
                        ? '#86b7fe'
                        : errors?.[namePrefix]?.quantity ||
                          errors?.[namePrefix]?.unit
                        ? '#dc3545'
                        : provided.borderColor,
                      boxShadow: state.isFocused
                        ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                        : provided.boxShadow,
                      '&:hover': {
                        borderColor: state.isFocused
                          ? '#86b7fe'
                          : errors?.[namePrefix]?.quantity ||
                            errors?.[namePrefix]?.unit
                          ? '#dc3545'
                          : provided.borderColor
                      }
                    }),
                    valueContainer: provided => ({
                      ...provided,
                      height: '36px',
                      padding: '0 8px'
                    }),
                    input: provided => ({
                      ...provided,
                      margin: '0px'
                    }),
                    indicatorsContainer: provided => ({
                      ...provided,
                      height: '36px'
                    })
                  }}
                />
              )}
            />
          </div>
        </div>
        {/* Combined error feedback - show if either field has an error */}
        {(errors?.[namePrefix]?.quantity || errors?.[namePrefix]?.unit) && (
          <div className="invalid-feedback d-block">
            {errors?.[namePrefix]?.quantity?.message ||
              errors?.[namePrefix]?.unit?.message ||
              `${metadata.label} is invalid`}
          </div>
        )}
      </Form.Group>
    </Col>
  );
};

export default TextAndUnitField;
